import { useCallback, useEffect, useState } from "react";

interface UseRetryIncompleteDataProps {
  data: {
    title?: string;
    topic?: string;
    ideaId?: string;
  };
  refetchData?: (ideaId: string) => void;
  maxRetries?: number;
  retryInterval?: number;
}

/**
 * Custom hook that automatically retries fetching data when title or topic is missing.
 *
 * @param data - The data object containing title, topic, and ideaId
 * @param refetchData - Function to call when retrying (should be provided by parent)
 * @param maxRetries - Maximum number of retry attempts (default: 5)
 * @param retryInterval - Time between retries in milliseconds (default: 3000)
 */
export const useRetryIncompleteData = ({
  data,
  refetchData,
  maxRetries = 5,
  retryInterval = 3000,
}: UseRetryIncompleteDataProps) => {
  const [retryCount, setRetryCount] = useState(0);

  const { title, topic, ideaId } = data || {};

  // Check if data is missing and needs to be refetched
  const isDataIncomplete = !title || !topic;

  // Retry mechanism for missing data
  useEffect(() => {
    if (isDataIncomplete && retryCount < maxRetries && ideaId && refetchData) {
      const timer = setTimeout(() => {
        setRetryCount((prev) => prev + 1);
        refetchData(ideaId);
      }, retryInterval);

      return () => clearTimeout(timer);
    }
  }, [
    isDataIncomplete,
    retryCount,
    ideaId,
    refetchData,
    maxRetries,
    retryInterval,
  ]);

  // Reset retry count when data becomes complete
  useEffect(() => {
    if (!isDataIncomplete && retryCount > 0) {
      setRetryCount(0);
    }
  }, [isDataIncomplete, retryCount]);

  return {
    retryCount,
    isDataIncomplete,
    maxRetries,
  };
};
